import React, { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AppProvider } from "@/lib/store";
import { Toaster } from "@/components/ui/sonner";
import { AUTH_ROUTES } from "@/features/auth";
import { WORKSPACE_ROUTES } from "@/features/workspace";
import { PermissionRoute, ProtectedRoute, PublicOnlyRoute } from "@/routes/guards";
import { LocalizationProvider } from "@/lib/localization";
import { PlatformConfigurationProvider } from "@/lib/platformConfiguration";
import { SubscriptionReadOnlyDialog } from "@/components/subscription/SubscriptionReadOnlyDialog";
import { InsufficientCreditsDialog } from "@/components/credits/InsufficientCreditsDialog";
import { startBrowserLocationTracking, stopBrowserLocationTracking } from "@/lib/location";

export default function App() {
  useEffect(() => {
    startBrowserLocationTracking();
    const restartTracking = () => startBrowserLocationTracking();
    const handleVisibility = () => {
      if (document.visibilityState === "visible") restartTracking();
    };
    window.addEventListener("focus", restartTracking);
    document.addEventListener("visibilitychange", handleVisibility);
    return () => {
      window.removeEventListener("focus", restartTracking);
      document.removeEventListener("visibilitychange", handleVisibility);
      stopBrowserLocationTracking();
    };
  }, []);

  return (
    <LocalizationProvider>
      <PlatformConfigurationProvider>
		<BrowserRouter>
          <AppProvider>
            <div className="App">
            <Routes>
              {AUTH_ROUTES.map(({ path, element: Element }) => (
                <Route
                  key={path}
                  path={path}
                  element={<PublicOnlyRoute><Element /></PublicOnlyRoute>}
                />
              ))}

              <Route
                path="/subscribe"
                element={<Navigate to="/subscription" replace />}
              />

              <Route element={<ProtectedRoute />}>
                {WORKSPACE_ROUTES.map(({ path, element: Element, permission, role }) => (
                  <Route key={path} path={path} element={(
                    <PermissionRoute permission={permission} role={role}>
                      <Element />
                    </PermissionRoute>
                  )} />
                ))}
              </Route>

              <Route path="/auth/*" element={<AuthRootRedirect />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
            <SubscriptionReadOnlyDialog />
            <InsufficientCreditsDialog />
            <Toaster position="top-right" richColors closeButton />
            </div>
          </AppProvider>
		</BrowserRouter>
      </PlatformConfigurationProvider>
    </LocalizationProvider>
  );
}

function AuthRootRedirect() {
  const { search } = useLocation();
  return <Navigate to={`/${search}`} replace />;
}
